import React from "react";
import { List } from "components";
import JobNameCell from "@/components/cells/jobs/name-cell";
import { JOB_TYPES } from "@/lib/constants";
import { formatDateForDistance, ROUTE, calculateTotalHours } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "ui/components/tooltip";
import { Icon } from "components";
import { DateCell } from "@/components/date/cell";
import { EmployeeCell } from "components/cell/employee/cell";
import { UtilizationCell } from "@/components/cells/jobs/utilization";
import { HoursCell } from "@/components/hours/cell";
import ExtendedJobNameCell from "@/components/cells/jobs/name-cell/extended-name";
import { getEmployeeFields } from "components/user/config";

const InReviewJobsList = ({ coordinatorId }) => {
  const fields = [
    "id",
    "name",
    "status",
    "type",
    "access.employee",
    "access.role",
    getEmployeeFields("coordinator"),
    "project.name",
    "project.id",
    "project.project_id",
    "start_date",
    "end_date",
    getEmployeeFields("project.coordinator"),
    getEmployeeFields("project.sales_coordinator"),
    getEmployeeFields("project.account_manager"),
    "allocated_hours.hours",
    "bucket_job_config.hours",
    "timesheet.logged_hours",
    "timesheet.employee.consumption_factor",
    "timesheet.consumption_factor",
  ];

  const columns = [
    {
      id: "name",
      header: "Name",
      accessorKey: "name",
      sortable: true,
      cell: ExtendedJobNameCell,
    },
    {
      id: "project",
      header: "Project",
      accessorKey: "project.name",
    },
    {
      id: "type",
      header: "Type",
      accessorKey: "type",
      cell: ({ getValue }) => {
        const type = JOB_TYPES[getValue()];
        if (!type) return null;
        return (
          <Tooltip>
            <TooltipTrigger>
              <Icon icon={type.icon} className="text-muted-foreground size-4" />
            </TooltipTrigger>
            <TooltipContent>{type.label}</TooltipContent>
          </Tooltip>
        );
      },
      sortable: false,
    },
    {
      id: "start_date",
      header: "Start Date",
      accessorKey: "start_date",
      sortable: true,
      cell: DateCell,
    },
    {
      id: "end_date",
      header: "End Date",
      accessorKey: "end_date",
      sortable: true,
      cell: DateCell,
    },
    {
      id: "extensions",
      header: "Extended",
      accessorKey: "extensions",
      cell: (info) => {
        const extensions = info.row.original.extensions;
        if (!Array.isArray(extensions) || extensions.length === 0) return null;

        const end_date = info.row.original.end_date;
        const startDate = extensions[0].start_date;
        const endDate = end_date;

        const isNegative = endDate < startDate;
        const days = isNegative
          ? formatDateForDistance(startDate, endDate)
          : formatDateForDistance(endDate, startDate);

        const displayDays = isNegative ? -Math.abs(days) : days;
        return `${displayDays} day${Math.abs(displayDays) === 1 ? "" : "s"}`;
      },
    },
    {
      id: "coordinator",
      header: "JC",
      accessorKey: "coordinator",
      cell: EmployeeCell,
    },
    {
      id: "project.coordinator",
      header: "PC",
      accessorKey: "project.coordinator",
      cell: EmployeeCell,
    },
    {
      id: "project.account_manager",
      header: "AM",
      accessorKey: "project.account_manager",
      cell: EmployeeCell,
    },
    {
      id: "project.sales_coordinator",
      header: "SC",
      accessorKey: "project.sales_coordinator",
      cell: EmployeeCell,
    },
    {
      id: "timesheet.logged_hours",
      header: "Logged",
      accessorKey: "timesheet.logged_hours",
      cell: ({ row }) => (
        <HoursCell
          getValue={() =>
            calculateTotalHours(row.original.timesheet || [], "logged_hours")
          }
        />
      ),
    },
    {
      id: "hours_totals",
      header: () => {
        return (
          <div className="flex flex-col">
            <span>Utilization</span>
            <span className="text-xs text-muted-foreground">
              Spent / Allocated
            </span>
          </div>
        );
      },
      accessorKey: "timesheet",
      cell: UtilizationCell,
    },
  ];

  const filters = {
    access: {
      employee: {
        _eq: coordinatorId,
      },
      role: {
        _in: ["account_manager", "sales_coordinator", "project_coordinator", "job_coordinator"]
      },
    },
    status: {
      _eq: "in_review",
    },
  };

  const deep = {
    extensions: {
      _sort: ["date_created"],
    },
  };

  return (
    <div key={coordinatorId}>
      <List
        title="In Review Jobs"
        storageKey={`overview-jobs-${coordinatorId}`}
        endPoint="jobs"
        filters={filters}
        columns={columns}
        meta={{ fields, deep }}
        sortOrder="asc"
        perPage={25}
        sortBy="project.name"
        href={(item) => ROUTE.JOB(item.id)}
        hidePagination={true}
        showSearch={false}
      />
    </div>
  );
};

export default InReviewJobsList;
